import { Inject, Injectable } from '@angular/core';
import { DepositAssetsBaseService } from '@coin-depo-core/deposit-assets';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ApiService, CommonApiService } from '@coin-depo/http';
import { StorageService } from '@coin-depo-core/storage';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import type { DepositPlanDto, DepositPlanAlt, NewDepositAssetsPlan, DepositAssetDto, NewDepositAssetsResponse, DepositPlansResponse, DepositAssetsResponse, DepositPlansAlt } from '@coin-depo-types';
import { ExtendedApiResponse, DepositAssetsRequest, DepositPlansRequest } from '@coin-depo-types';
import { AUTH_SERVICE_TOKEN, AuthFacade } from '@coin-depo-core/auth';

@Injectable({
  providedIn: 'root',
})
export class DepositAssetsService extends DepositAssetsBaseService {
  protected depositPlans$ = new BehaviorSubject<DepositPlanDto[]>(
    this.storage.get('depositPlans', [])
  );

  protected depositPlansAlt$ = new BehaviorSubject<DepositPlanAlt[]>(
    this.storage.get('depositPlansAlt', [])
  );

  protected depositDropdown$ = new BehaviorSubject<NewDepositAssetsPlan[]>(
    this.storage.get('depositDropdown', [])
  );

  cachedDepositPlansSelect$ = this.cachedDepositPlans$.pipe(
    map((plans) =>
      plans.map((item: DepositPlanDto) => {
        const regex = /[^0-9.]/g;
        item.coinsText = `${parseFloat(item.coins.replace(regex, ''))}%`;
        item.stableText = `APR: ${parseFloat(item.stable.replace(regex, ''))}%`;
        return item;
      })
    )
  );

  constructor(
    @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
    private balancesApi: ApiService,
    private commonApi: CommonApiService,
    private storage: StorageService
  ) {
    super();
  }

  getDepositAssets(id: number): Observable<DepositAssetDto[]> {
    const userData = this.auth.userData;

    const data: DepositAssetsRequest = {
      client_token: this.auth.accessToken,
      username: userData.email,
      account_type_id: id,
    };

    return this.balancesApi.getDepositAssets(data).pipe(
      map((res: ExtendedApiResponse<DepositAssetsResponse>) =>
        res.status === 'success' && res.plans ? res.plans : []
      ),
      catchError((err: HttpErrorResponse) => of(err.error.msg))
    );
  }

  updateNewDepositDropdown(): Observable<any[]> {
    const userData = this.auth.userData;

    const data = {
      client_token: this.auth.accessToken,
      username: userData.email,
    };

    return this.balancesApi.getNewDepositAssets(data).pipe(
      map((res: ExtendedApiResponse<NewDepositAssetsResponse>) =>
        res.status === 'success' && res.plans ? res.plans : []
      ),
      catchError((err: HttpErrorResponse) => of(err.error.msg)),
      tap((res) => {
        this.depositDropdown$.next(res);
        this.storage.set('depositDropdown', res);
      })
    );
  }

  getDepositDropdownById(
    id: string | number
  ): Observable<NewDepositAssetsPlan[]> {
    return this.cachedDepositDropdown$.pipe(
      map((plans) => plans.filter((plan) => plan.coin_id === Number(id)))
    );
  }

  getSnapshotDepositDropdownById(
    id: string | number
  ): NewDepositAssetsPlan[] {
    return this.depositDropdown$.value.filter(
      (plan) => plan.coin_id === Number(id)
    );
  }

  updateDepositPlans(): Observable<DepositPlanDto[] | string> {
    const userData = this.auth.userData;

    const query: DepositPlansRequest = {
      client_token: this.auth.accessToken,
      username: userData.username || userData.email,
    };

    return this.commonApi.getDepositPlans(query).pipe(
      map((res: ExtendedApiResponse<DepositPlansResponse>) =>
        res.status === 'success' && res.plans ? res.plans : res.msg
      ),
      catchError((err: HttpErrorResponse) => of(err.error.msg)),
      tap((res) => {
        if (typeof res !== 'string') {
          this.depositPlans$.next(res);
          this.storage.set('depositPlans', res);
        }
      })
    );
  }

  updateDepositPlansAlt(): Observable<DepositPlansAlt> {
    const userData = this.auth.userData;

    const query: DepositPlansRequest = {
      client_token: this.auth.accessToken,
      username: userData.username || userData.email,
    };

    return this.commonApi.getDepositPlansAlt(query).pipe(
      tap((res) => {
        this.depositPlansAlt$.next(res.plans);
        this.storage.set('depositPlansAlt', res.plans);
      })
    );
  }
}